// Making Tax Digital for VAT (UK books): connect to HMRC, see the periods HMRC
// expects returns for, send one with the legal declaration, and keep HMRC's
// receipt. Figures come from the ledger; nothing can be typed over them.
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Banner, Button, Card, Dialog, formatMoney, PageHeader, Pill, type Tone } from '@elysian/ui';
import type { HmrcStatus, MtdObligation, MtdReceipt, MtdVatReturn } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { longDate, shortDate } from '../invoices/shared';

const STATE: Record<MtdObligation['status'], { tone: Tone; label: (o: MtdObligation) => string }> = {
  open: { tone: 'draft', label: () => 'Period still open' },
  due: { tone: 'warn', label: (o) => `Due ${shortDate(o.due)}` },
  overdue: { tone: 'danger', label: (o) => `Overdue · was due ${shortDate(o.due)}` },
  fulfilled: { tone: 'ok', label: (o) => `Filed${o.received ? ` ${shortDate(o.received)}` : ''}` },
};

// The nine boxes, worded as on HMRC's VAT return.
const BOXES: [keyof MtdVatReturn, string, string][] = [
  ['vatDueSales', '1', 'VAT due on sales and other outputs'],
  ['vatDueAcquisitions', '2', 'VAT due on acquisitions of goods from the EU (Northern Ireland)'],
  ['totalVatDue', '3', 'Total VAT due (boxes 1 and 2)'],
  ['vatReclaimedCurrPeriod', '4', 'VAT reclaimed on purchases and other inputs'],
  ['netVatDue', '5', 'Net VAT to pay to HMRC or reclaim (difference between boxes 3 and 4)'],
  ['totalValueSalesExVAT', '6', 'Total value of sales, excluding VAT'],
  ['totalValuePurchasesExVAT', '7', 'Total value of purchases, excluding VAT'],
  ['totalValueGoodsSuppliedExVAT', '8', 'Goods supplied to the EU, excluding VAT (Northern Ireland)'],
  ['totalAcquisitionsExVAT', '9', 'Goods acquired from the EU, excluding VAT (Northern Ireland)'],
];

/** HMRC's required wording, shown before every submission. */
export const DECLARATION = 'When you submit this VAT information you are making a legal declaration that the information is true and complete. ' +
  'A false declaration can result in prosecution.';

const pounds = (v: number, whole: boolean) => formatMoney(v.toFixed(whole ? 0 : 2), 'GBP', { places: whole ? 0 : 2 });
const when = (s: string) => new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }).format(new Date(s));

export function MtdVat() {
  const { source } = useSession();
  const toast = useToast((s) => s.show);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [version, setVersion] = useState(0);
  const status = useAsync(() => source.hmrcStatus(), [source, version], { keep: true });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Back from HMRC's sign-in page.
  const outcome = params.get('hmrc');
  useEffect(() => {
    if (!outcome) return;
    if (outcome === 'connected') toast('Connected to HMRC');
    else if (outcome === 'cancelled') toast('HMRC sign-in cancelled');
    else setError(params.get('message') ?? 'HMRC didn’t sign you in.');
    setParams({}, { replace: true });
    setVersion((v) => v + 1);
  }, [outcome, params, setParams, toast]);

  async function connect() {
    setBusy(true);
    setError(null);
    try {
      const { authorise_url } = await source.connectHmrc();
      if (authorise_url.startsWith('/')) navigate(authorise_url);
      else window.location.assign(authorise_url);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function disconnect() {
    setBusy(true);
    try {
      await source.disconnectHmrc();
      toast('Disconnected from HMRC. Returns already sent are kept.');
      setVersion((v) => v + 1);
    } finally {
      setBusy(false);
    }
  }

  const st = status.state === 'ready' ? status.data : null;
  return (
    <>
      <PageHeader title="VAT returns" subtitle={`Making Tax Digital · sent straight to HMRC${st?.vrn ? ` · VAT registration ${st.vrn}` : ''}`}
        actions={st?.sandbox ? <Pill tone="info">HMRC sandbox (test)</Pill> : undefined} />
      {status.state === 'error' && <Banner>Couldn’t load the HMRC connection: {status.error.message}</Banner>}
      {error && <div className="el-error-box" role="alert" style={{ marginBottom: 16 }}>{error}</div>}
      {st && !st.connected && <ConnectCard status={st} busy={busy} onConnect={() => void connect()} />}
      {st?.connected && <Obligations key={version} status={st} onDisconnect={() => void disconnect()} onReconnect={() => void connect()} />}
    </>
  );
}

function ConnectCard({ status, busy, onConnect }: { status: HmrcStatus; busy: boolean; onConnect: () => void }) {
  const blocked = !status.vrn || !status.available;
  return (
    <Card as="section" padding="20px" aria-labelledby="hmrc-connect">
      <h2 id="hmrc-connect" className="el-h2" style={{ marginBottom: 8 }}>Connect to HMRC</h2>
      <p style={{ marginTop: 0 }}>
        UK VAT returns are sent to HMRC from here under Making Tax Digital. Sign in once with the Government Gateway user ID for
        this business’s VAT registration; the connection lasts 18 months.
      </p>
      {status.problem && <Banner tone="info">{status.problem}.</Banner>}
      {!status.configured && <Banner>This server doesn’t have HMRC credentials yet (Hmrc:ClientId and Hmrc:ClientSecret).</Banner>}
      <div className="el-row" style={{ marginTop: 12 }}>
        <Button variant="primary" disabled={busy || blocked || !status.configured} onClick={onConnect}>Connect to HMRC</Button>
      </div>
    </Card>
  );
}

function Obligations({ status, onDisconnect, onReconnect }: { status: HmrcStatus; onDisconnect: () => void; onReconnect: () => void }) {
  const { source } = useSession();
  const [version, setVersion] = useState(0);
  const list = useAsync(() => source.mtdObligations(), [source, version], { keep: true });
  const [selected, setSelected] = useState<string | null>(null);
  const items = list.state === 'ready' ? list.data.obligations : [];
  const current = items.find((o) => o.period_key === selected)
    ?? items.find((o) => o.status === 'overdue' || o.status === 'due') ?? items[0];

  return (
    <>
      {items.filter((o) => o.status === 'overdue').map((o) => (
        <Banner key={o.period_key}>The return for {longDate(o.start)} to {longDate(o.end)} was due on {longDate(o.due)}.</Banner>
      ))}
      {list.state === 'error' && <Banner action={<Button size="sm" onClick={onReconnect}>Connect again</Button>}>Couldn’t get your returns from HMRC: {list.error.message}</Banner>}
      <div className="el-two-col">
        <Card as="section" padding="16px" aria-labelledby="mtd-periods">
          <div className="el-spread" style={{ marginBottom: 10 }}>
            <h2 id="mtd-periods" className="el-h2">Returns HMRC expects</h2>
          </div>
          {list.state === 'loading' && <div className="el-skeleton" style={{ height: 120 }} aria-label="Asking HMRC" />}
          <div className="el-stack" style={{ gap: 6 }}>
            {items.map((o) => (
              <button key={o.period_key} type="button" className="el-pick" aria-current={current?.period_key === o.period_key ? 'true' : undefined}
                onClick={() => setSelected(o.period_key)}>
                <span className="el-pick-main">{shortDate(o.start)} – {shortDate(o.end)}</span>
                <Pill tone={STATE[o.status].tone}>{STATE[o.status].label(o)}</Pill>
              </button>
            ))}
          </div>
          <div className="el-spread" style={{ marginTop: 14, alignItems: 'center' }}>
            <span className="el-hint">
              Connected {status.connected_at ? shortDate(status.connected_at.slice(0, 10)) : ''}
              {status.expires_at && <> · until {shortDate(status.expires_at.slice(0, 10))}</>}
            </span>
            <Button size="sm" variant="ghost" onClick={onDisconnect}>Disconnect</Button>
          </div>
        </Card>
        {current && <Detail key={current.period_key} o={current} onSent={() => setVersion((v) => v + 1)} />}
      </div>
    </>
  );
}

function Detail({ o, onSent }: { o: MtdObligation; onSent: () => void }) {
  const { source, can } = useSession();
  const toast = useToast((s) => s.show);
  const sentHere = !!o.form_bundle_number;
  const receipt = useAsync(() => (sentHere ? source.mtdReceipt(o.period_key) : Promise.resolve(null)), [source, o.period_key, sentHere]);
  const [confirming, setConfirming] = useState(false);
  const [declared, setDeclared] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justSent, setJustSent] = useState<MtdReceipt | null>(null);
  const figures = justSent?.submitted ?? (receipt.state === 'ready' ? receipt.data?.submitted : null) ?? o.figures;
  const shown = justSent ?? (receipt.state === 'ready' ? receipt.data : null);
  const canSend = (o.status === 'due' || o.status === 'overdue') && !!o.figures && can('vat.file') && !justSent;

  async function send() {
    setBusy(true);
    setError(null);
    try {
      const r = await source.submitMtdReturn(o.period_key, o.start, o.end);
      setJustSent(r);
      setConfirming(false);
      toast(`Return sent to HMRC. Form bundle number ${r.receipt.formBundleNumber}.`);
      onSent();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card as="section" padding="18px 20px" aria-labelledby="mtd-detail">
      <div className="el-spread" style={{ marginBottom: 12 }}>
        <div>
          <h2 id="mtd-detail" className="el-h2">{shortDate(o.start)} to {shortDate(o.end)}</h2>
          <div className="el-hint">HMRC period {o.period_key} · due {longDate(o.due)}</div>
        </div>
        <Pill tone={justSent ? 'ok' : STATE[o.status].tone}>{justSent ? 'Sent today' : STATE[o.status].label(o)}</Pill>
      </div>

      {shown && <ReceiptPanel r={shown} />}
      {o.status === 'fulfilled' && !sentHere && !justSent && (
        <p className="el-muted">HMRC received this return{o.received ? ` on ${longDate(o.received)}` : ''}. It was sent from other software, so its figures aren’t held here.</p>
      )}
      {o.status === 'open' && <p className="el-muted">The period ends on {longDate(o.end)}. The return can be sent from the next day.</p>}

      {figures && (
        <div className="el-table-wrap">
          <table className="el-table" aria-label="VAT return boxes">
            <thead><tr><th scope="col">Box</th><th scope="col">What it is</th><th scope="col" className="el-num">Amount</th></tr></thead>
            <tbody>
              {BOXES.map(([key, n, label]) => (
                <tr key={key}>
                  <td><strong>{n}</strong></td>
                  <td>{label}</td>
                  <td className="el-num">{pounds(Number(figures[key] ?? 0), Number(n) >= 6)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {figures && !shown && (
        <p className="el-hint">Boxes 1 to 5 are to the penny; boxes 6 to 9 are in whole pounds, as HMRC asks. The figures come from the books and can’t be typed over: correct the invoices or bills instead.</p>
      )}
      {canSend && (
        <div className="el-row" style={{ justifyContent: 'flex-end' }}>
          <Button variant="primary" onClick={() => { setError(null); setDeclared(false); setConfirming(true); }}>Review and send to HMRC</Button>
        </div>
      )}

      {confirming && o.figures && (
        <Dialog title="Send this VAT return to HMRC?" onClose={() => setConfirming(false)}
          actions={<><Button variant="ghost" onClick={() => setConfirming(false)}>Not yet</Button>
            <Button variant="primary" disabled={!declared || busy} onClick={() => void send()}>{busy ? 'Sending…' : 'Send to HMRC'}</Button></>}>
          <p style={{ marginTop: 0 }}>
            {o.figures.totalVatDue >= o.figures.vatReclaimedCurrPeriod ? 'To pay' : 'To reclaim'}: <strong>{pounds(o.figures.netVatDue, false)}</strong>.
            Sending locks {longDate(o.start)} to {longDate(o.end)}, so nothing in those months can change afterwards.
          </p>
          <p className="el-hint">{DECLARATION}</p>
          <label className="el-check"><input type="checkbox" checked={declared} onChange={(e) => setDeclared(e.target.checked)} />
            I confirm the information is true and complete</label>
          {error && <div className="el-error-box" role="alert">{error}</div>}
        </Dialog>
      )}
    </Card>
  );
}

function ReceiptPanel({ r }: { r: MtdReceipt }) {
  const pay = r.receipt.paymentIndicator === 'DD' ? 'HMRC will collect by Direct Debit'
    : r.receipt.paymentIndicator === 'BANK' ? 'HMRC will repay to the business’s bank account' : null;
  return (
    <section aria-label="HMRC receipt" className="el-receipt" style={{ border: '1px solid var(--el-border)', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
      <div className="el-label" style={{ marginBottom: 6 }}>HMRC’s receipt</div>
      <dl className="el-kv">
        <dt>Received by HMRC</dt><dd>{when(r.receipt.processingDate)}</dd>
        <dt>Form bundle number</dt><dd>{r.receipt.formBundleNumber}</dd>
        {r.receipt.chargeRefNumber && <><dt>Charge reference</dt><dd>{r.receipt.chargeRefNumber}</dd></>}
        {pay && <><dt>Payment</dt><dd>{pay}</dd></>}
        <dt>VAT registration</dt><dd>{r.vrn}</dd>
      </dl>
    </section>
  );
}
